import { useCallback, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronRight, Star } from "lucide-react";
import { CATEGORIES } from "../data/categories";
import { TOOLS } from "../data/tools";
import { useData } from "../context/DataContext";
import { useReveal } from "../hooks/useReveal";
import { stuckPick } from "../engine/suggest";
import { MASCOT_BULB } from "../lib/tones";
import { ResultReveal } from "../components/reveal/ResultReveal";
import { CoinPanel, DicePanel } from "../components/home/QuickPanels";
import { PresentPanel } from "../components/home/PresentPanel";
import { SITE_URL, faqJsonLd, useSeo } from "../lib/seo";
import { HOME_SEO, categoryAlt } from "../data/homeSeo";
import { ChristmasBanner } from "../components/home/ChristmasBanner";
import { HomeSeoSection } from "../components/seo/HomeSeoSection";
import { LmsBanner } from "../components/home/LmsBanner";

function Burst({ flip = false }: { flip?: boolean }) {
  return (
    <span className={`hidden flex-col gap-2 sm:flex ${flip ? "items-start" : "items-end"}`} aria-hidden>
      <i className={`block h-2 w-7 rounded bg-[#FF5D1F] ${flip ? "rotate-[25deg]" : "-rotate-[25deg]"}`} />
      <i className="block h-2 w-10 rounded bg-[#FF8A2B]" />
      <i className={`block h-2 w-7 rounded bg-[#FF5D1F] ${flip ? "-rotate-[25deg]" : "rotate-[25deg]"}`} />
    </span>
  );
}

function Masthead() {
  return (
    <header className="relative pb-1 pt-3 text-center sm:pt-4" data-testid="home-masthead">
      <div className="flex items-center justify-center gap-3">
        <Burst />
        <h1 data-testid="home-h1">
          <span className="sr-only">{HOME_SEO.h1}</span>
          <span aria-hidden className="ua-logo block text-[3.4rem] leading-none sm:text-7xl lg:text-8xl" data-testid="home-logo">Dither</span>
        </h1>
        <Burst flip />
      </div>
      <span className="ua-highlight -mt-1 text-base sm:text-xl" data-testid="home-tagline">Can't decide? Dither!</span>
      <div className="absolute right-0 top-3 hidden rotate-[-8deg] items-center gap-1 rounded-xl bg-[#FFE14D] px-3 py-2 shadow-md md:flex" data-testid="home-sticker">
        <p className="font-comic text-sm leading-tight text-[#3b1800]">Same people<br />better decisions</p>
        <span className="text-2xl" aria-hidden>☺</span>
      </div>
    </header>
  );
}

function CategoryRow() {
  const { prefs, setPrefs } = useData();
  const favs = prefs.favourite_categories || [];
  const sorted = [...CATEGORIES].sort((a, b) => Number(favs.includes(b.id)) - Number(favs.includes(a.id)));
  const toggle = (id: string) => {
    const next = favs.includes(id) ? favs.filter((f) => f !== id) : [...favs, id];
    setPrefs({ favourite_categories: next });
    toast.success(next.includes(id) ? "Pinned — DITHER will pick it more often" : "Unpinned");
  };
  return (
    <motion.section layout id="home-categories" className="scroll-mt-20 grid grid-cols-3 gap-2 sm:gap-3 lg:grid-cols-6" data-testid="category-grid">
      {sorted.map((c) => {
        const fav = favs.includes(c.id);
        return (
          <motion.div layout key={c.id} transition={{ type: "spring", stiffness: 500, damping: 35 }} className="relative">
            <Link to={c.id === "presents" ? "/presents" : `/category/${c.id}`} data-testid={`category-card-${c.id}`}
              className={`ua-tile relative block aspect-[4/5] overflow-hidden border-[3px] ${c.border}`}>
              <img src={c.cover} alt={categoryAlt(c.id)} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent px-1 pb-2 pt-10 text-center">
                <p className="ua-title text-xl sm:text-3xl">{c.name}</p>
                <p className="mt-0.5 text-[10px] font-bold text-white sm:text-xs">{c.question}</p>
              </div>
              {fav && <span className="absolute left-2 top-2 rounded-full bg-[#FFE14D] px-2 font-comic text-[10px] text-[#3b1800]" data-testid={`category-fav-badge-${c.id}`}>Go-to</span>}
            </Link>
            <button onClick={() => toggle(c.id)} aria-pressed={fav} aria-label={fav ? `Unfavourite ${c.name}` : `Favourite ${c.name}`} data-testid={`category-fav-toggle-${c.id}`}
              className="absolute right-1.5 top-1.5 rounded-full bg-white/90 p-1 shadow transition-transform hover:scale-110 active:scale-90">
              <Star className={`h-3.5 w-3.5 ${fav ? "fill-[#FFB000] text-[#B87300]" : "text-[#6b3a12]"}`} strokeWidth={2.5} />
            </button>
          </motion.div>
        );
      })}
    </motion.section>
  );
}

function BoredBanner({ onGo }: { onGo: () => void }) {
  return (
    <button onClick={onGo} data-testid="im-stuck-global-button"
      className="ua-tile group relative flex w-full items-center gap-2 overflow-hidden rounded-[22px] border-[4px] border-[#FFC15E] bg-gradient-to-r from-[#FF7A1A] via-[#FF8A2B] to-[#FF6A1F] p-2 pr-4 text-left sm:gap-4">
      <img src={MASCOT_BULB} alt="Light bulb mascot cheering" className="h-24 w-24 shrink-0 rounded-2xl object-cover transition-transform group-hover:rotate-[-6deg] sm:h-32 sm:w-32" />
      <span className="flex-1">
        <span className="ua-title block text-4xl sm:text-6xl">I'm bored</span>
        <span className="mt-1 inline-block rotate-[-2deg] rounded-md bg-[#FFB347] px-3 font-comic text-base text-[#5a1e00] sm:text-xl">Surprise me!</span>
      </span>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-[3px] border-[#3b1800] bg-[#FFE14D] transition-transform group-hover:translate-x-1 sm:h-14 sm:w-14">
        <ChevronRight className="h-7 w-7 text-[#3b1800]" strokeWidth={4} />
      </span>
    </button>
  );
}

function ToolArt({ art }: { art: string }) {
  if (art.startsWith("/")) return <img src={art} alt="" className="h-12 w-12 object-contain transition-transform group-hover:scale-110 group-hover:rotate-6 sm:h-14 sm:w-14" />;
  return <span className={`ua-logo whitespace-nowrap ${art.length > 2 ? "text-xl sm:text-2xl" : "text-3xl sm:text-4xl"}`}>{art}</span>;
}

function ToolRow() {
  return (
    <section className="grid grid-cols-4 gap-2 sm:grid-cols-8" data-testid="tool-row">
      {TOOLS.map((t) => (
        <Link key={t.id} to={`/tools/${t.id}`} data-testid={`quick-tool-${t.id}`}
          className={`ua-tile group flex flex-col items-center justify-between gap-1 border-[3px] px-1 py-2 text-center ${t.tile}`}>
          <span className="flex h-14 items-center sm:h-16"><ToolArt art={t.art} /></span>
          <span className="font-comic text-[11px] leading-tight text-[#3b1800] sm:text-sm">{t.name}</span>
        </Link>
      ))}
      <Link to="/wheels" data-testid="quick-tool-my-wheels" className="ua-tile group flex flex-col items-center justify-between gap-1 border-[3px] border-[#FFB36B] bg-[#FFE0C2] px-1 py-2 text-center">
        <span className="flex h-14 items-center sm:h-16"><ToolArt art="/icons/wheel.png" /></span>
        <span className="font-comic text-[11px] leading-tight text-[#3b1800] sm:text-sm">My wheels</span>
      </Link>
    </section>
  );
}

function GroupStrip() {
  return (
    <Link to="/group/new" data-testid="home-group-promo" className="ua-tile flex items-center gap-3 border-[3px] border-[#C9A3FF] bg-gradient-to-r from-[#7C3AED] to-[#9B5CFF] p-3 text-white">
      <img src="/icons/group.png" alt="" className="h-16 w-16 shrink-0 object-contain" />
      <span className="flex-1">
        <span className="ua-title block text-2xl sm:text-3xl">Settle it with friends</span>
        <span className="text-xs font-bold text-white/90 sm:text-sm">Poll, share the link, vote by name — live. Tie? DITHER breaks it.</span>
      </span>
      <ChevronRight className="h-7 w-7 shrink-0" strokeWidth={4} />
    </Link>
  );
}

export default function HomePage({ autoStuck = false }: { autoStuck?: boolean }) {
  const navigate = useNavigate();
  const { prefs, recordDislike } = useData();
  const { spec, show, close } = useReveal();
  const ran = useRef(false);
  useSeo({
    title: HOME_SEO.title, description: HOME_SEO.description, ogTitle: HOME_SEO.ogTitle, keywords: HOME_SEO.keywords, ogDescription: HOME_SEO.ogDescription,
    path: "/", noindex: autoStuck,
    jsonLd: [{ "@context": "https://schema.org", "@type": "WebApplication", name: "DITHER", url: `${SITE_URL}/`, applicationCategory: "LifestyleApplication", operatingSystem: "Any", offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" } }, faqJsonLd(HOME_SEO.faqs)],
  });

  const runStuck = useCallback((exclude?: string) => {
    const s = stuckPick(prefs.dislikes, exclude, prefs.favourite_categories);
    show({ kind: "stuck", title: `Surprise me · ${s.category.name}`, result: s.result, anim: "slot", reel: s.reel, more: s.more, category: s.category.id });
  }, [prefs.dislikes, prefs.favourite_categories, show]);

  useEffect(() => {
    if (autoStuck && !ran.current) {
      ran.current = true;
      runStuck();
    }
  }, [autoStuck, runStuck]);

  return (
    <div className="mx-auto max-w-[1320px] space-y-2 px-2 pb-4 sm:space-y-3 sm:px-3" data-testid="home-page">
      <Masthead />
      <ChristmasBanner />
      <CategoryRow />
      <div className="grid gap-2 sm:gap-3 lg:grid-cols-[1fr_1.25fr]">
        <BoredBanner onGo={() => window.dispatchEvent(new Event("dither:pick-number"))} />
        <ToolRow />
      </div>
      <div className="grid gap-2 sm:gap-3 md:grid-cols-3">
        <CoinPanel />
        <DicePanel />
        <PresentPanel />
      </div>
      <GroupStrip />
      <HomeSeoSection />
      <LmsBanner />
      <ResultReveal spec={spec}
        onClose={() => { close(); if (autoStuck) navigate("/", { replace: true }); }}
        onAgain={() => { if (spec) { recordDislike(spec.result); runStuck(spec.result); } }} />
    </div>
  );
}
